import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, isRef as _isRef, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }

import { ref as _ref } from 'vue';

import {
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonRow,
    modalController,
} from "@ionic/vue";
import { ref, onMounted } from "vue";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { t } from "@/plugins/vue-i18n";
import { request } from "@/api-connectors/basicRequests";
import { useThrottleFn } from "@vueuse/core";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import InputSimple from "@/components/input/InputSimple.vue";
import ButtonFix from "@/components/button/ButtonFix.vue";
import SelectSimple from "@/components/selects/SelectSimple.vue";
import FeedbackBug from "@/components/form/FeedbackBug.vue";
import FeedbackFeature from "@/components/form/FeedbackFeature.vue";

import { replaceToast } from "@/functions/baseToast";

import { useLoader } from "@/composables/useLoader";
import { useLanguageArray } from "@/composables/useLanguageArray";

import { FeedbackTicketTemplate } from "@/types/request";
import { ISSUE_TYPE, TICKET_PRIORITY, TICKET_CONTACT_WAY } from "@/types/enum/general";
import { ION_VARIANT } from "@/types/enum/ionicEnum";

import { chevronBack } from "ionicons/icons";
import { useUserStore } from "@/stores/user";


export default /*@__PURE__*/_defineComponent({
  __name: 'FeedbackModal',
  setup(__props) {
let name = _ref<string>("");
const issueType = _ref<ISSUE_TYPE>(ISSUE_TYPE.BUG);
const priority = _ref<TICKET_PRIORITY>(TICKET_PRIORITY.MEDIUM);

const profile = useUserStore().sessionInfo
const contactWay = _ref<{ way: TICKET_CONTACT_WAY, email: string, phone: string }>({ way: TICKET_CONTACT_WAY.EMAIL, email: profile.email ?? "", phone: "" })
let descriptionTemplate = _ref<FeedbackTicketTemplate>(
    new FeedbackTicketTemplate(),
);
const files = _ref<File[]>([]);
const feedbackBugEl = ref();
const feedbackFeatureEl = ref();

const { showLoading, hideLoading } = useLoader();

const resetValues = (ref: any): void => {
    name.value = "";
    descriptionTemplate.value = new FeedbackTicketTemplate();
    files.value.length = 0;
    loadVersions();
    ref.resetForm();
};

const { content: issueTypeOptions } = useLanguageArray(
    [
        { text: `enumT.issueType.${ISSUE_TYPE.BUG}`, value: ISSUE_TYPE.BUG },
        {
            text: `enumT.issueType.${ISSUE_TYPE.FEATURE}`,
            value: ISSUE_TYPE.FEATURE,
        },
    ],
    "text",
);

const { content: contactWayOptions } = useLanguageArray(
    [
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.NONE}`, value: TICKET_CONTACT_WAY.NONE },
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.EMAIL}`, value: TICKET_CONTACT_WAY.EMAIL },
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.PHONE}`, value: TICKET_CONTACT_WAY.PHONE },
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.EMAIL_OR_PHONE}`, value: TICKET_CONTACT_WAY.EMAIL_OR_PHONE },
    ],
    "text",
);

const sendTicket = async () => {
    await showLoading();

    const formData = new FormData();

    for (const [key, value] of Object.entries(descriptionTemplate.value)) {
        if (value !== undefined) {
            formData.append(key, value);
        }
    }

    if (files.value.length > 0) {
        for (const file of files.value) {
            formData.append("files", file);
        }
    }

    formData.append("contactWay", contactWay.value.way)
    if (contactWay.value.way === TICKET_CONTACT_WAY.EMAIL || contactWay.value.way === TICKET_CONTACT_WAY.EMAIL_OR_PHONE) {
        formData.append("contactEmail", contactWay.value.email)
    }

    if (contactWay.value.way === TICKET_CONTACT_WAY.PHONE || contactWay.value.way === TICKET_CONTACT_WAY.EMAIL_OR_PHONE) {
        formData.append("contactPhone", contactWay.value.phone)
    }

    formData.append("name", name.value);
    formData.append("priority", priority.value);
    formData.append("type", issueType.value);

    const response = await request({
        method: "POST",
        route: "feedback/mobile/ticket",
        body: formData,
    });

    if (response === undefined) {
        await replaceToast({
            message: t("error.sendTicket"),
            color: ION_VARIANT.DANGER,
            id: "error-issue-report",
        });
        await hideLoading();
        return;
    }

    await modalController.dismiss();

    replaceToast({
        message: t("success.sendTicket"),
        id: "success-issue-report",
    });
    await hideLoading();
};

const throttleTicket = useThrottleFn(sendTicket, 1000);

const addFiles = (inputFiles: FileList | File | undefined): void => {
    if (!inputFiles) return;
    Object.values(inputFiles).forEach((file) => {
        files.value.push(file);
    });
};

const loadVersions = async () => {
    descriptionTemplate.value.deviceId = (await Device.getId()).identifier;
    descriptionTemplate.value.webVersion =
        process.env.VUE_APP_VERSION_NAME ?? "UNKNOWN";

    const {
        model,
        operatingSystem,
        osVersion,
        webViewVersion,
        platform,
        manufacturer,
    } = await Device.getInfo();
    descriptionTemplate.value.deviceModel = `${manufacturer}, ${model} (${platform})`;
    descriptionTemplate.value.osVersion = `${operatingSystem} ${osVersion}`;
    descriptionTemplate.value.webViewVersion = webViewVersion;

    if (Capacitor.isNativePlatform()) {
        const { build, version } = await App.getInfo();
        descriptionTemplate.value.binaryVersion = `${version} (${build})`;
    }
};

onMounted(loadVersions);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalControllerBase, { contentClass: "light-light-background purple-purple-color" }, {
    header: _withCtx(() => [
      _createVNode(CustomHeader, {
        "back-button": "",
        title: _ctx.$t('titleRoute.feedback')
      }, {
        start: _withCtx(() => [
          _createVNode(_unref(IonButton), {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(modalController).dismiss())),
            class: "purple-light-color"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonIcon), { icon: _unref(chevronBack) }, null, 8, ["icon"]),
              _createTextVNode(" " + _toDisplayString(_ctx.$t("button.back")), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["title"])
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(IonGrid), { class: "p-3 max-height max-height-100 overflow-auto d-flex flex-d-c" }, {
        default: _withCtx(() => [
          _createVNode(_unref(IonRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(IonCol), { class: "fs-smaller grey1-grey1-color" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t(`placeholder.issueType`)), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(IonCol), null, {
                default: _withCtx(() => [
                  _createVNode(SelectSimple, {
                    placeholder: _ctx.$t('placeholder.issueType'),
                    options: _unref(issueTypeOptions),
                    modelValue: _unref(issueType),
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(issueType) ? (issueType).value = $event : null)),
                    onChange: _cache[2] || (_cache[2] = ($event: any) => (
                            resetValues(
                                _unref(issueType) === _unref(ISSUE_TYPE).BUG
                                    ? feedbackBugEl.value
                                    : feedbackFeatureEl.value,
                            )
                            )),
                    "select-class": "grey4-grey4-background"
                  }, null, 8, ["placeholder", "options", "modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(IonCol), { class: "fs-smaller grey1-grey1-color" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t(`placeholder.title`)) + "* ", 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(IonCol), null, {
                default: _withCtx(() => [
                  _createVNode(InputSimple, {
                    modelValue: _unref(name),
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(name) ? (name).value = $event : name = $event)),
                    "input-class": "grey4-grey4-background"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(FeedbackBug, {
            modelValue: _unref(descriptionTemplate),
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(descriptionTemplate) ? (descriptionTemplate).value = $event : descriptionTemplate = $event)),
            files: _unref(files),
            onFile: addFiles,
            ref_key: "feedbackBugEl",
            ref: feedbackBugEl,
            show: _unref(issueType) === _unref(ISSUE_TYPE).BUG
          }, null, 8, ["modelValue", "files", "show"]),
          _createVNode(FeedbackFeature, {
            modelValue: _unref(descriptionTemplate),
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(descriptionTemplate) ? (descriptionTemplate).value = $event : descriptionTemplate = $event)),
            ref_key: "feedbackFeatureEl",
            ref: feedbackFeatureEl,
            show: _unref(issueType) === _unref(ISSUE_TYPE).FEATURE
          }, null, 8, ["modelValue", "show"]),
          _createVNode(_unref(IonRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(IonCol), { class: "fs-smaller grey1-grey1-color" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t(`placeholder.contactWay`)), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(IonCol), null, {
                default: _withCtx(() => [
                  _createVNode(SelectSimple, {
                    placeholder: _ctx.$t('placeholder.contactWay'),
                    options: _unref(contactWayOptions),
                    modelValue: _unref(contactWay).way,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(contactWay).way) = $event)),
                    "select-class": "grey4-grey4-background"
                  }, null, 8, ["placeholder", "options", "modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          (_unref(contactWay).way === _unref(TICKET_CONTACT_WAY).EMAIL || _unref(contactWay).way === _unref(TICKET_CONTACT_WAY).EMAIL_OR_PHONE)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(IonRow), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonCol), { class: "fs-smaller grey1-grey1-color" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t(`placeholder.email`)), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_unref(IonRow), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonCol), null, {
                      default: _withCtx(() => [
                        _createVNode(InputSimple, {
                          modelValue: _unref(contactWay).email,
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(contactWay).email) = $event)),
                          "validate-option": { name: 'email', label: _ctx.$t('label.email') },
                          "input-class": "grey4-grey4-background"
                        }, null, 8, ["modelValue", "validate-option"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          (_unref(contactWay).way === _unref(TICKET_CONTACT_WAY).PHONE || _unref(contactWay).way === _unref(TICKET_CONTACT_WAY).EMAIL_OR_PHONE)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_unref(IonRow), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonCol), { class: "fs-smaller grey1-grey1-color" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t(`placeholder.phone`)), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_unref(IonRow), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonCol), null, {
                      default: _withCtx(() => [
                        _createVNode(InputSimple, {
                          modelValue: _unref(contactWay).phone,
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(contactWay).phone) = $event)),
                          "validate-option": { name: 'phone', label: _ctx.$t('label.phone') },
                          "input-class": "grey4-grey4-background"
                        }, null, 8, ["modelValue", "validate-option"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          _createVNode(_unref(IonRow), { class: "flex-grow-1" }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonCol), { class: "center-x ion-align-items-end" }, {
                default: _withCtx(() => [
                  _createVNode(ButtonFix, {
                    onClick: _unref(throttleTicket),
                    disabled: !(_unref(issueType) === _unref(ISSUE_TYPE).BUG
                        ? feedbackBugEl.value?.meta?.valid
                        : feedbackFeatureEl.value?.meta?.valid) || !_unref(name)
                        
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t("button.send")), 1)
                    ]),
                    _: 1
                  }, 8, ["onClick", "disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})